import { useState, useEffect, useCallback } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Combobox from "@/components/Combobox";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QUICK_ADD } from "@/config/quickadd";

const SOURCE_MAP = {
  users: ["/users", (d) => d.items.map((u) => ({ value: u.id, label: u.name }))],
  territories: ["/territories", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
  zones: ["/zones", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
  lead_statuses: ["/lead_statuses", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
  lead_sources: ["/lead_sources", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
  pipeline_stages: ["/pipeline_stages", (d) => [...d.items].sort((a, b) => (a.order || 0) - (b.order || 0)).map((t) => ({ value: t.name, label: t.name }))],
  products: ["/products", (d) => d.items.map((t) => ({ value: t.name, label: t.name }))],
  businesses: ["/businesses", (d) => d.items.map((t) => ({ value: t.id, label: t.name }))],
  contacts: ["/contacts", (d) => d.items.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name || ""}`.trim(), business_id: c.business_id }))],
};

async function loadSources(keys) {
  const out = {};
  await Promise.all(keys.map(async (k) => {
    if (!SOURCE_MAP[k]) return;
    try { const { data } = await api.get(SOURCE_MAP[k][0]); out[k] = SOURCE_MAP[k][1](data); }
    catch { out[k] = []; }
  }));
  return out;
}

export default function QuickAddDrawer({ open, entity, prefill, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const cfg = entity ? QUICK_ADD[entity] : null;
  const [sources, setSources] = useState({});
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);

  // Lazily load option sources the first time a drawer opens.
  useEffect(() => {
    if (!open || !cfg) return;
    const need = new Set();
    cfg.fields.forEach((f) => f.source && need.add(f.source));
    loadSources([...need]).then((s) => setSources((prev) => ({ ...prev, ...s })));
  }, [open, entity]); // eslint-disable-line

  // Initialise form (defaults + prefill) whenever the drawer opens.
  useEffect(() => {
    if (!open || !cfg) return;
    const base = {};
    cfg.fields.forEach((f) => {
      if (f.default !== undefined) base[f.name] = f.default;
      else base[f.name] = "";
    });
    if (base.assigned_to === "") base.assigned_to = user?.id || "";
    Object.assign(base, cfg.defaults || {});
    setForm({ ...base, ...(prefill || {}) });
    setDupes([]);
  }, [open, entity]); // eslint-disable-line

  // Fill required selects (status/stage) with the first available option once sources arrive.
  useEffect(() => {
    if (!open || !cfg) return;
    setForm((prev) => {
      const next = { ...prev };
      cfg.fields.forEach((f) => {
        if (f.defaultFirst && !next[f.name] && sources[f.source]?.[0]) next[f.name] = sources[f.source][0].value;
      });
      return next;
    });
  }, [sources, open]); // eslint-disable-line

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const checkDupes = useCallback(async () => {
    if (!cfg?.dedupe) return;
    const payload = {};
    cfg.fields.filter((f) => f.dedupe).forEach((f) => { if (form[f.name]) payload[f.name] = form[f.name]; });
    if (!Object.keys(payload).length) return;
    try {
      const { data } = await api.post("/duplicate-check", payload);
      setDupes([...(data.duplicates || []), ...(data.lead_matches || [])]);
    } catch { /* non-blocking */ }
  }, [cfg, form]);

  const comboOptions = (f) => {
    let opts = sources[f.source] || [];
    if (f.scopeByBusiness && form.business_id) opts = opts.filter((o) => o.business_id === form.business_id);
    return opts;
  };

  const submit = async () => {
    for (const f of cfg.fields) {
      if (f.required && !form[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setSaving(true);
    try {
      const payload = {};
      cfg.fields.forEach((f) => {
        let v = form[f.name];
        if (v === "" || v == null) return;
        if (f.type === "number") v = Number(v);
        payload[f.name] = v;
      });
      Object.assign(payload, cfg.defaults || {});
      // Attach display names for the backend timeline / list views.
      if (payload.business_id) {
        const b = (sources.businesses || []).find((x) => String(x.value) === String(payload.business_id));
        if (b) payload.business_name = b.label;
      }
      if (payload.contact_id) {
        const c = (sources.contacts || []).find((x) => String(x.value) === String(payload.contact_id));
        if (c) payload.contact_name = c.label;
      }
      const { data } = await api.post(cfg.endpoint, payload);
      toast.success(`${cfg.label} created`);
      onOpenChange(false);
      onSaved && onSaved(data, entity);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  const Icon = cfg.icon;

  const renderField = (f) => {
    const common = { "data-testid": `qa-field-${f.name}` };
    if (f.type === "textarea")
      return <Textarea className="mt-1" value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} {...common} />;
    if (f.type === "combo")
      return <Combobox options={comboOptions(f)} value={form[f.name]} onChange={(v) => set(f.name, v)} placeholder={`Select ${f.label.toLowerCase()}…`} testid={`qa-field-${f.name}`} />;
    if (f.type === "select") {
      const opts = f.source ? (sources[f.source] || []) : (f.options || []).map((o) => ({ value: o, label: o }));
      return (
        <Select value={form[f.name] ? String(form[f.name]) : ""} onValueChange={(v) => set(f.name, v)}>
          <SelectTrigger className="mt-1" {...common}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    const type = f.type === "datetime" ? "datetime-local" : f.type === "number" ? "number" : f.type === "date" ? "date" : "text";
    return (
      <Input type={type} className="mt-1" value={form[f.name] ?? ""}
        onChange={(e) => set(f.name, e.target.value)}
        onBlur={f.dedupe ? checkDupes : undefined} {...common} />
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="quick-add-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 text-left">
          <SheetTitle className="font-display flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#00C9E0]/12 text-[#0094A8] flex items-center justify-center"><Icon className="h-4 w-4" /></span>
            New {cfg.label}
          </SheetTitle>
          <SheetDescription>Create a {cfg.label.toLowerCase()} without leaving this page.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {dupes.length > 0 && (
            <div className="mb-4 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3 text-xs text-[#B45309]" data-testid="qa-dupe-warning">
              <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Possible duplicate{dupes.length > 1 ? "s" : ""} found</p>
              <ul className="mt-1 list-disc ml-4">
                {dupes.slice(0, 4).map((d) => <li key={d.id}>{d.name || d.business_name}{d.city ? ` · ${d.city}` : ""}</li>)}
              </ul>
              <p className="mt-1 text-[#92400E]">You can still save if this is a new record.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cfg.fields.map((f) => (
              <div key={f.name} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-medium text-slate-600">{f.label}{f.required && <span className="text-[#FE3967]"> *</span>}</Label>
                {renderField(f)}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qa-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="qa-save">{saving ? "Saving…" : `Create ${cfg.label}`}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
